import React, { useState, useEffect } from "react";
import { Table, Button, Select, Space, Col, DatePicker, Row, Form, Input } from "antd";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { useAppSelector } from "../../..";
import { checkIsLoaded, checkIsLoading } from "../../../lib/utils/checkLoadingStatus";
import { toDisplayFormatWithTime } from "../../../lib/utils/formatDate";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import AuthService from "../../../services/AuthService";
import { baseUrl } from "../../../lib/routes/baseUrl";
import "./TaskList.less";

const { RangePicker } = DatePicker;

const TaskList = ({
  setCurrentPage,
  currentPage,
  setCurrentTaskId,
  getTask,
  getUsers,
  formUsers,
  filters,
  setFilters,
  getTasks,
  setIsModalVisible,
  setMainTask,
  setFormUsers,
}) => {
  const [form] = Form.useForm();
  const {
    tasks: { totalElements, content: tasks },
    taskTypes: { content: taskTypes },
    tasksLoading,
    initiators,
    initiatorsLoading,
  } = useAppSelector((state) => state.tasks);
  // const [isModalVisible, setIsModalVisible] = useState(false);
  const { taskStatus: taskStatuses, userScope: userScopes } = useAppSelector(
    (state) => state.rtkDictionaries
  );
  const [tableData, setTableData] = useState([]);

  const [columns, setColumns] = useState([
    {
      title: "ID задания",
      dataIndex: "id",
      key: "id",
      hidden: true,
    },
    {
      title: "Вид задания",
      dataIndex: "taskType",
      key: "taskType",
      className: "table__column",
    },
    {
      title: "Инициатор",
      dataIndex: "initiator",
      key: "initiator",
      className: "table__column",
    },
    {
      title: "Дата создания",
      dataIndex: "createDate",
      key: "createDate",
      className: "table__column",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      className: "table__column",
    },
    {
      title: "Количество попыток",
      dataIndex: "retry",
      key: "retry",
      className: "table__column",
      hidden: true,
    },
    {
      title: "Комментарий",
      dataIndex: "statusDescription",
      key: "statusDescription",
      className: "table__column",
      hidden: true,
    },
  ]);

  useEffect(() => {
    if (checkIsLoaded(initiatorsLoading, tasksLoading) && Array.isArray(tasks)) {
      setTableData(
        tasks.map(({ type, createDate, statusId, login, id, userId, retry, statusDescription }) => {
          const taskType = taskTypes.find(({ alias }) => String(alias) === String(type));
          const status = taskStatuses.find(({ id: stId }) => String(stId) === String(statusId));
          const initiator = initiators.find((item) => item.id === userId);
          return {
            key: id,
            id,
            taskType: taskType?.title,
            initiator: initiator?.userInfo?.middleName
              ? `${initiator?.userInfo?.lastName} ${initiator?.userInfo?.firstName} ${initiator?.userInfo?.middleName}`
              : `${initiator?.userInfo?.lastName} ${initiator?.userInfo?.firstName}`,
            login,
            createDate: createDate && toDisplayFormatWithTime(createDate),
            status: status?.name,
            retry,
            statusDescription,
          };
        })
      );
    }
  }, [initiators, initiatorsLoading, taskStatuses, taskTypes, tasks, tasksLoading]);

  const userName = jwtDecode(AuthService.getCurrentUser());

  useEffect(() => {
    if (userName?.display_name) {
      axios
        .get(`${baseUrl}/security-api/users/pageable-search`, {
          params: {
            fullName: userName.display_name,
          },
        })
        .then((res) => {
          setFormUsers(res.data.content);

          form.setFieldsValue({
            ...filters,
          });
        });
    }
  }, []);

  const handleSubmit = () => {
    setCurrentPage(1);
    setFilters(form.getFieldsValue());
    getTasks(form.getFieldsValue());
  };

  const handleReset = () => {
    setFilters({ userScopeId: [1, 3] });
    setCurrentPage(1);
    getTasks({ userScopeId: [1, 3] });
    form.resetFields(["type", "createDate", "status", "id"]);
  };

  const handleSearch = (value) => {
    if (value) {
      getUsers(value);
    }
  };

  const renderFilterForm = () => (
    <Form
      name="tasks-search"
      form={form}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 17 }}
      labelAlign="left"
      style={{ border: "1px solid rgba(68, 204, 255, 0.5)", padding: 15, paddingTop: 30 }}
      initialValues={filters}
      onFinish={() => handleSubmit(filters)}
      onReset={() => handleReset()}
    >
      <Row justify="start" name="taskType">
        <Col span={10}>
          <Form.Item label="Вид задания" name="type">
            <Select allowClear>
              {taskTypes
                ?.filter(({ userScopeId }) => {
                  return userScopeId !== 2;
                })
                ?.map(({ id, title, alias }) => (
                  <Select.Option value={alias} key={alias}>
                    {title}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label="Дата создания" name="createDate">
            <RangePicker />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item label="Область видимости" name="userScopeId">
            <Select showSearch={false} mode="multiple" showArrow allowClear>
              {userScopes
                ?.filter(({ id }) => {
                  return id !== 2;
                })
                ?.map(({ id, name }) => (
                  <Select.Option value={id} key={id}>
                    {name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label="Статус задания" name="status">
            <Select showSearch={false} value={null} mode="multiple" showArrow allowClear>
              {taskStatuses.map(({ id, name }) => (
                <Select.Option value={id} key={id}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row justify="start">
        <Col span={10}>
          <Form.Item
            label="ID заданий"
            name="id"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "В качестве разделителя может использоваться только запятая",
              },
            ]}
          >
            <Input allowClear placeholder='Введите ID заданий через ","' />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item label="Инициатор" name="initiator">
            <Select
              showSearch
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              onChange={handleSearch}
              onSearch={handleSearch}
              notFoundContent={null}
              allowClear
            >
              {formUsers.map((user) => (
                <Select.Option value={user.id} key={user.id}>
                  {user?.userInfo?.middleName
                    ? `${user?.userInfo?.lastName} ${user?.userInfo?.firstName} ${user?.userInfo?.middleName}`
                    : `${user?.userInfo?.lastName} ${user?.userInfo?.firstName}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {/* <Col span={10}>
            <Form.Item
              label="ID подзаданий"
              name="subtask-id"
              rules={[
                {
                  validator: (x, value) => {
                    if (value && /[^0-9,]/.test(value)) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: "В качестве разделителя может использоваться только запятая",
                },
              ]}
            >
              <Input allowClear placeholder='Введите ID подзаданий через ","'/>
            </Form.Item>
        </Col> */}
      </Row>
      <Row justify="end">
        <Col>
          <Space>
            <Form.Item>
              <Button onClick={handleReset} htmlType="reset" style={{ width: 146 }}>
                Сброс параметров
              </Button>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: 146 }}>
                Применить
              </Button>
            </Form.Item>
          </Space>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div className="container_height container">
      <div className="tasks-form">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Список заданий</h1>
          </Col>
        </Row>
      </div>
      {renderFilterForm()}
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns tableName="task-table" columns={columns} setColumns={setColumns} />
      </Row>
      <Table
        loading={!checkIsLoaded(tasksLoading) || checkIsLoading(initiatorsLoading)}
        columns={columns.filter(({ hidden }) => !hidden)}
        size="small"
        tableLayout="fixed"
        dataSource={tableData}
        pagination={{
          position: ["bottomCenter"],
          defaultPageSize: 20,
          hideOnSinglePage: true,
          onChange: (current) => setCurrentPage(current),
          total: totalElements,
          current: currentPage,
          showSizeChanger: false,
        }}
        rowClassName="table__row"
        onRow={(record) => ({
          onClick: () => {
            setCurrentTaskId(record.key);
            setMainTask(record.key);
            setIsModalVisible(true);
            getTask(record.key);
          },
        })}
      />
    </div>
  );
};

export default TaskList;
